import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import Link from '@mui/material/Link';
import { Outlet } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useBranding } from '@eis/core';
import { SUITE_ACCENT_VARIABLE } from '@eis/theme';
import { usePermissions } from '@eis/patterns';
import { AppBreadcrumbs } from './AppBreadcrumbs';
import { AppNav } from './AppNav';
import { AppTopBar } from './AppTopBar';
import { DemoNoticeDialog } from './DemoNoticeDialog';
import { ModuleErrorBoundary } from './ModuleErrorBoundary';
import { useNavCollapsed } from './useNavCollapsed';
import { useChromeVariant } from './useChromeVariant';
import { activeModuleRegistry } from '../app/modules';
import { NoAccessPage } from '../pages/NoAccessPage';

/**
 * The tenant accent, applied the way the legacy applies it: `:root { --main-color: <hex> }`
 * (`ui-utils.ts::setColorMain`). The suite theme reads the variable for the active nav item and
 * its marks, so a tenant's colour reaches every mark without a theme rebuild. Suite only — Berry
 * has its own palette and no tenant colour.
 */
function TenantAccent(): ReactNode {
  const branding = useBranding();
  return <GlobalStyles styles={{ ':root': { [SUITE_ACCENT_VARIABLE]: branding.mainColor } }} />;
}

/** The chrome every route renders inside. */
export function AppShell(): ReactNode {
  const { t } = useTranslation('shell');
  const permissions = usePermissions();
  const { collapsed, toggle } = useNavCollapsed();
  const suite = useChromeVariant().variant === 'suite';

  /**
   * No module grants anything: explain it, but keep the top bar.
   *
   * This is the screen where an operator most needs to know WHICH account and WHICH tenant they
   * are signed in as — that is usually the whole answer to "why can I not see anything" — and it
   * is the only screen from which they must still be able to sign out. The navigation is dropped
   * because it would be empty, and with it the toggle that would open an empty drawer.
   */
  if (!activeModuleRegistry().hasAnyAccess(permissions)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        {suite && <TenantAccent />}
        <AppTopBar />
        <NoAccessPage />
        <DemoNoticeDialog />
      </Box>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}
    >
      {suite && <TenantAccent />}
      <DemoNoticeDialog />
      {/* Keyboard users should not have to tab through the whole nav on every page. */}
      <Link
        href="#main-content"
        sx={{
          position: 'absolute',
          left: -9999,
          top: 8,
          zIndex: 1400,
          p: 1,
          bgcolor: 'background.paper',
          '&:focus': { left: 8 },
        }}
      >
        {t('app.skipToContent')}
      </Link>

      <Box id="app-nav" sx={{ display: 'flex' }}>
        <AppNav collapsed={collapsed} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <AppTopBar onToggleNav={toggle} navCollapsed={collapsed} navId="app-nav" />
        <AppBreadcrumbs />
        <Box id="main-content" sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <ModuleErrorBoundary
            moduleId="shell"
            title={t('moduleError.title')}
            description={t('moduleError.description')}
          >
            <Outlet />
          </ModuleErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
}
